import i18n from "../../i18n";
import { formatCurrency } from "../../hooks/Currency";

const PRINT_AGENT_URL = "http://localhost:5055";

export const getPrintAgentStatus = async () => {
  try {
    const response = await fetch(
      `${PRINT_AGENT_URL}/status`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      return {
        running: false,
        version: null,
      };
    }

    const result = await response.json();

    if (
      result.status !== 200 ||
      result.running !== true
    ) {
      return {
        running: false,
        version: null,
      };
    }

    return result;
  } catch (err) {
    return {
      running: false,
      version: null,
    };
  }
};

export const printReceiptDirect = async (
  printerName,
  receiptText,
  paperWidth = 80
) => {
  const response = await fetch(
    `${PRINT_AGENT_URL}/print-receipt`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        printerName,
        receiptText,
        paperWidth,
      }),
    }
  );

  const result = await response.json();

  if (!response.ok || result.status !== 200) {
    throw new Error(
      result.message || "Direct printing failed."
    );
  }
  return result;
};

export const buildReceiptText = (
  data,
  store,
  authUser,
  companyName = ""
) => {
  const t = (key) => i18n.t(key);

  const receiptLocale =
    i18n.resolvedLanguage?.startsWith("fr")
      ? "fr-FR"
      : i18n.resolvedLanguage?.startsWith("es")
      ? "es-ES"
      : "en-US";
    
  const paymentLabels = {
    1: t("cash"),
    2: t("card"),
    3: t("stripe"),
    4: t("check"),
    5: t("payLater"),
    6: t("split"),
    7: t("other"),
  };

  const paymentLabel =
    paymentLabels[data?.payType] || t("other");
  
  const storeTimeZone =
    store?.timeZone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const sellDate = data?.sellDate
    ? new Date(
        /(?:Z|[+-]\d{2}:\d{2})$/i.test(data.sellDate)
          ? data.sellDate
          : `${data.sellDate}Z`
      ).toLocaleString(receiptLocale, {
        timeZone: storeTimeZone,
      })
    : "";

  const line = "------------------------------------------";
  const receiptWidth = line.length;

  const amountLine = (label, amount) => {
    const value = formatCurrency(amount, store);
    const spaces = Math.max(
      1,
      receiptWidth - label.length - value.length
    );

    return `${label}${" ".repeat(spaces)}${value}\n`;
  };

  let receipt = "";

  // Header
  if (companyName) {
    receipt += `${companyName.toUpperCase()}\n`;
  }

  receipt += `${store?.storeName || ""}\n`;
  receipt += `${store?.address || ""}\n`;
  receipt += `${store?.phone || ""}\n`;

  receipt += `${line}\n`;


  // Sale information
  receipt += `${t("servedBy")}: ${authUser?.firstName || ""}\n`;
  receipt += `${t("invoiceNo")}: ${data?.id || ""}\n`;
  receipt += `${t("date")}: ${sellDate}\n`;
  receipt += `${t("paymentType")}: ${paymentLabel}\n`;
  receipt += `${t("customer")}: ${
    data?.customer?.customerName || t("walkInCustomer")
  }\n`;

  receipt += `${line}\n`;

  // Products
  receipt += `${t("itemName")} | ${t("qty")} | ${t("price")} | ${t("total")}\n`;
  receipt += `${line}\n`;

  if (data?.sellItems) {
    data.sellItems.forEach((item) => {
      const itemName =
        item.itemType === 1
          ? item.product?.name || ""
          : t("discount");

      const qty = Number(item.qty || 0);
      const price = Number(item.price || 0);
      const total = qty * price;

      receipt += `${itemName}\n`;
      receipt += `  ${t("qty")}: ${qty}  ${t("price")}: ${formatCurrency(price, store)}\n`;
      receipt += `  ${t("total")}: ${formatCurrency(total, store)}\n`;
    });
  }

  receipt += `${line}\n`;

  const subtotal = Number(data?.subtotal || 0);
  const tax = Number(data?.tax || 0);
  const total = subtotal + tax;

  receipt += amountLine(`${t("subtotal")}:`, subtotal);
  receipt += amountLine(`${t("tax")} (${store?.tax || 0}%):`, tax);
  receipt += `${line}\n`;
  receipt += amountLine(`${t("total").toUpperCase()}:`, total);
  receipt += `${line}\n`;

  if (Number(data?.cashAmount || 0) > 0) {
    receipt += amountLine(`${t("cash")}:`, Number(data.cashAmount));
  }

  if (Number(data?.cardAmount || 0) > 0) {
    receipt += amountLine(`${t("card")}:`, Number(data.cardAmount));
  }

  receipt += amountLine(
    `${t("paymentReceived")}:`,
    Number(data?.received || 0)
  );

  if (Number(data?.cashAmount || 0) > 0) {
    receipt += amountLine(
      `${t("change")}:`,
      Number(data?.change || 0)
    );
  }

  receipt += "\n";
  receipt += `${t("thankYouForShopping")}\n`;
  receipt += `${t("pleaseComeAgain")}\n`;

  if (companyName) {
    receipt += `\n`;
   // receipt += `www.${companyName.replace(/\s+/g, "")}.com\n`;
  }

  return receipt;
};

export const printReceiptHtmlDirect = async (
  printerName,
  receiptHtml,
  paperWidth = 80
) => {
  const response = await fetch(
    "http://localhost:5055/print-html",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        printerName,
        receiptHtml,
        paperWidth,
      }),
    }
  );

  const result = await response.json();

  if (!response.ok || result.status !== 200) {
    throw new Error(
      result.message || "HTML direct printing failed."
    );
  }

  return result;
};

export const buildReceiptHtml = (
  receiptElement,
  paperWidth = 80
) => {
  if (!receiptElement) {
    throw new Error(
      "print Sell Receipt element was not found."
    );
  }
  
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />

        <style>
          @page {
            margin: 0;
          }

          html,
          body {
            margin: 0;
            padding: 0;
          }

          body {
            box-sizing: border-box;
            width: ${paperWidth || 80}mm;

            padding-top: 10mm;
            padding-left: 10mm;
            padding-right: 3mm;
          }

          :root {
            --grayLight-color: #cccccc;
          }
        </style>
      </head>

      <body>
        ${receiptElement.innerHTML}
      </body>
    </html>
  `;
};


/**
 * Build printable HTML for A4 or Letter reports.
 * Does not affect POS receipt printing.
 */

export const buildReportHtml = (
  reportElement,
  paperSize = "A4"
) => {
  if (!reportElement) {
    throw new Error("Report content was not found.");
  }

  const selectedPaper =
    String(paperSize).toUpperCase() === "LETTER"
      ? "Letter"
      : "A4";

  // Copy the report HTML.
  const reportHtml = reportElement.outerHTML;

  // Copy all styles currently loaded in the React application,
  // including Material UI / Emotion-generated CSS.
  const styleElements = Array.from(
    document.querySelectorAll(
      'style, link[rel="stylesheet"]'
    )
  );

  const styles = styleElements
    .map((element) => element.outerHTML)
    .join("\n");

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />

        <base href="${document.baseURI}" />

        ${styles}

        <style>
          @page {
            size: ${selectedPaper} portrait;
            margin: 12mm;
          }

          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            background: white !important;
            color: black !important;
          }

          body {
            font-family: Arial, sans-serif;
            font-size: 11px;
          }

          .customer-report-print {
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Compact Customer Report printing */
          .customer-report-print .MuiGrid-container {
            row-gap: 0 !important;
          }

          .customer-report-print .MuiGrid-item {
            padding-top: 0 !important;
          }

          .customer-report-print .MuiCardContent-root {
            padding: 6px 10px !important;
          }

          .customer-report-print .MuiCardContent-root:last-child {
            padding-bottom: 6px !important;
          }

          .customer-report-print .MuiTypography-root {
            margin-top: 0 !important;
            margin-bottom: 2px !important;
          }

          .customer-report-print .MuiGrid-container {
            margin-bottom: 8px !important;
          }

          .customer-report-print .MuiTableCell-root {
            padding: 4px 6px !important;
          }

          .no-print {
            display: none !important;
          }

          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }

          thead {
            display: table-header-group;
          }

          tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          th,
          td {
            overflow-wrap: anywhere;
          }

          .MuiPaper-root {
            box-shadow: none !important;
          }

          .MuiCard-root {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          .refund-report-print .refund-print-table {
            display: block !important;
          }

          .refund-report-print .no-print {
            display: none !important;
          }

          .refund-report-print .MuiTableCell-root {
            padding: 4px 6px !important;
            font-size: 10px !important;
          }

          .refund-report-print .MuiCardContent-root {
            padding: 6px 10px !important;
          }

          .refund-report-print .MuiCardContent-root:last-child {
            padding-bottom: 6px !important;
          }

          /* Customer Account report */
          .customer-account-print .no-print {
            display: none !important;
          }

          .customer-account-print .MuiCardContent-root {
            padding: 8px 12px !important;
          }

          .customer-account-print .MuiCardContent-root:last-child {
            padding-bottom: 8px !important;
          }

          .customer-account-print .MuiTableCell-root {
            padding: 4px 6px !important;
          }

          .customer-account-print .MuiGrid-item {
            padding-top: 4px !important;
          }

          .customer-account-print .MuiCard-root {
            box-shadow: none !important;
            break-inside: avoid;
          }

          /* Register Details printing */
          .register-details-print .no-print {
            display: none !important;
          }

          .register-details-print .MuiGrid-root {
            box-sizing: border-box;
          }

          .register-details-print .MuiTypography-root {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }

          .register-details-print .MuiDivider-root {
            margin-top: 3px !important;
            margin-bottom: 3px !important;
          }

          .register-details-print .mt-20,
          .register-details-print .mb-30,
          .register-details-print .mt-40 {
            margin-top: 5px !important;
            margin-bottom: 5px !important;
          }
          
          /* Store Report printing */
          .store-report-print {
            width: 100% !important;
          }

          .store-report-print .MuiGrid-container {
            row-gap: 0 !important;
          }

          .store-report-print .MuiGrid-item {
            padding-top: 6px !important;
          }

          .store-report-print .MuiPaper-root {
            box-shadow: none !important;
          }

          .store-report-print .MuiTableCell-root {
            padding: 5px 8px !important;
            font-size: 11px !important;
          }

          .store-report-print .MuiTypography-h4 {
            font-size: 20px !important;
          }

          .store-report-print .MuiTypography-h5 {
            font-size: 17px !important;
          }

          .store-report-print .MuiDivider-root {
            margin: 4px 0 !important;
          }

          .store-report-print .mt-20,
          .store-report-print .mb-20 {
            margin-top: 6px !important;
            margin-bottom: 6px !important;
          }

          .store-report-print .p-20 {
            padding: 10px !important;
          }
            
          button {
            color: black !important;
            text-decoration: none !important;
          }
        </style>
      </head>

      <body>
        ${reportHtml}
      </body>
    </html>
  `;
};

/**
 * Print A4/Letter HTML through the POS Print Agent.
 * Requires the updated C# Print Agent with PaperSize support.
 */

export const printReportHtmlDirect = async (
  printerName,
  reportHtml,
  paperSize = "A4",
  orientation = "portrait"
) => {
  const selectedPaper =
    String(paperSize).toUpperCase() === "LETTER"
      ? "Letter"
      : "A4";

  const selectedOrientation =
    String(orientation).toLowerCase() === "landscape"
      ? "landscape"
      : "portrait";

  const response = await fetch(
    `${PRINT_AGENT_URL}/print-html`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        printerName,
        receiptHtml: reportHtml,
        paperSize: selectedPaper,
        orientation: selectedOrientation,
      }),
    }
  );

  const result = await response.json();

  if (!response.ok || result.status !== 200) {
    throw new Error(
      result.message || "Report printing failed."
    );
  }

  return result;
};

